import React, { useEffect } from "react";
import styled from "@emotion/styled";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import useAxios from "@libs/axios/useAxios";
import { FILE_URL } from "@libs/apiUrl";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1rem",
});

const Input = styled.input({
  display: "none",
});

const UploadButton = styled.label({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  position: "relative",
  cursor: "pointer",
  border: "1px solid #ebebeb",
  paddingTop: "100%", // 1:1 비율 유지
});

const ButtonContent = styled.div({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const PreviewContainer = styled.div({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "1rem",
});

const ImagePreview = styled.div({
  position: "relative",
  width: "100%",
  paddingTop: "100%",
  border: "1px solid #ebebeb",
  overflow: "hidden",
});

const PreviewImage = styled.img({
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transform: "translate(-50%, -50%)",
});

const DeleteButton = styled.div({
  position: "absolute",
  top: 6,
  right: 8,
  cursor: "pointer",
});

type Props = {
  files: any;
  setFiles: (files: any) => void;
};

const FileUploadInput = ({ files, setFiles }: Props) => {
  const theme = useTheme();
  const { setValue, watch } = useFormContext();
  const { axiosInstance } = useAxios();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    const selectedFilesArray = Array.from(selectedFiles);

    // 최대 업로드 가능한 파일 수 제한
    if (selectedFilesArray.length + files.length > 10) {
      alert("최대 10개의 파일을 업로드할 수 있습니다.");
      return;
    }

    // FormData 객체 생성
    const formData = new FormData();
    selectedFilesArray.forEach((file) => {
      formData.append("files", file);
    });

    try {
      // 파일 업로드 요청
      const res = await axiosInstance.post(FILE_URL, formData);

      if (res.status === 200) {
        const uploadedFiles = res.data.data;
        const newFiles = uploadedFiles?.map((file: any) => file.url);

        const updatedFiles = [...files, ...newFiles];

        setFiles(updatedFiles);
        setValue("image_list", updatedFiles);
      } else {
        alert("파일 업로드에 실패했습니다.");
      }
    } catch (error) {
      console.error("파일 업로드 중 에러가 발생했습니다:", error);
    } finally {
      event.target.value = "";
    }
  };

  const deleteItem = async (index: number) => {
    const fileToDelete = files[index];

    const updatedFiles = files.filter(
      (_: any, fileIndex: number) => fileIndex !== index,
    );

    try {
      const res = await axiosInstance.delete(FILE_URL, {
        data: { file_url_list: [fileToDelete] },
      });

      if (res.status === 200) {
        setFiles(updatedFiles);
        setValue("image_list", updatedFiles);
      } else {
        alert("파일 삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("파일 삭제 중 에러가 발생했습니다:", error);
    }
  };

  return (
    <Container>
      <PreviewContainer>
        <Input
          type="file"
          id="file-upload"
          accept="image/*"
          multiple
          onChange={handleFileChange}
        />
        {watch("image_list")?.length < 10 && (
          <UploadButton htmlFor="file-upload">
            <ButtonContent>
              <Image src={theme.image.plus_gray.default} />
            </ButtonContent>
          </UploadButton>
        )}
        {watch("image_list")?.map((file: any, index: number) => (
          <ImagePreview key={index}>
            <PreviewImage src={file} alt={`Preview ${index}`} />

            <DeleteButton onClick={() => deleteItem(index)}>
              <Image src={theme.image.close_transparent.default} />
            </DeleteButton>
          </ImagePreview>
        ))}
      </PreviewContainer>
    </Container>
  );
};

export default FileUploadInput;
